import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  getDatevAccountNumber,
  createDatevAccountNumber,
  updateDatevAccountNumber,
  deleteDatevAccountNumber,
} from '../apis/datevAccountNumberApi';
import {
  CreateDatevAccountNumberRequest,
  UpdateDatevAccountNumberRequest,
} from '../apis/types';

export const useDatevAccountNumber = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching Datev account number
  const {
    data: accountNumber = null,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['datevAccountNumber'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getDatevAccountNumber(token);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for creating account number
  const createMutation = useMutation({
    mutationFn: async (data: CreateDatevAccountNumberRequest) => {
      const token = await getAccessTokenSilently();
      return createDatevAccountNumber(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datevAccountNumber'] });
    },
    onError: error => {
      console.error('Error creating Datev account number:', error);
    },
  });

  // Mutation for updating account number
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateDatevAccountNumberRequest;
    }) => {
      const token = await getAccessTokenSilently();
      return updateDatevAccountNumber(id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datevAccountNumber'] });
    },
    onError: error => {
      console.error('Error updating Datev account number:', error);
    },
  });

  // Mutation for deleting account number
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await getAccessTokenSilently();
      return deleteDatevAccountNumber(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datevAccountNumber'] });
    },
    onError: error => {
      console.error('Error deleting Datev account number:', error);
    },
  });

  const error =
    queryError?.message ||
    createMutation.error?.message ||
    updateMutation.error?.message ||
    deleteMutation.error?.message ||
    null;

  return {
    accountNumber,
    loading,
    creating: createMutation.isPending,
    updating: updateMutation.isPending,
    deleting: deleteMutation.isPending,
    error,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
    refresh: () => {
      queryClient.invalidateQueries({ queryKey: ['datevAccountNumber'] });
    },
  };
};
